import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, map, of } from 'rxjs';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SearchInputComponent,
  TableComponent,
  TableGlobalRowComponent,
} from '@custom-components';
import { APP_PATHS } from '@core/constants';
import { DeviceOverrideResponseModel } from '@core/models';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import { AlarmConfigService } from '@core/services/alarm-config.service';

export interface DeviceOverrideDeviceRow {
  id: string;
  deviceId: string;
  ldn: string;
  address: string;
  showInPortal: boolean;
  emailNotification: boolean;
  /** This device's own effective global values, resolved backend-side against its actual
   * owning partner — a Xemex viewer's device list can span multiple partners, each with its
   * own global config, so these can differ per row and must never be replaced by a single
   * dialog-wide global (see toDeviceOverrideRow). */
  ownerGlobalShowInPortal: boolean;
  ownerGlobalEmailNotification: boolean;
  [key: string]: unknown;
}

export interface DeviceOverridesDialogData {
  configGroup: string;
  alarmTypeLabel: string;
  globalShowInPortal: boolean;
  globalEmailNotification: boolean;
  devices: DeviceOverrideDeviceRow[];
}

export function toDeviceOverrideRow(
  override: DeviceOverrideResponseModel,
): DeviceOverrideDeviceRow {
  return {
    id: String(override.device_id),
    deviceId: String(override.device_id),
    ldn: override.ldn,
    address: override.address,
    showInPortal: override.show_in_portal ?? override.global_show_in_portal,
    emailNotification: override.email_enabled ?? override.global_email_enabled,
    ownerGlobalShowInPortal: override.global_show_in_portal,
    ownerGlobalEmailNotification: override.global_email_enabled,
  };
}

@Component({
  selector: 'xemex-device-overrides-dialog',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    SearchInputComponent,
    TableComponent,
    TableGlobalRowComponent,
  ],
  templateUrl: './device-overrides-dialog.component.html',
  styleUrl: './device-overrides-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DeviceOverridesDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<DeviceOverridesDialogComponent>);
  private readonly router = inject(Router);
  private readonly alarmConfigService = inject(AlarmConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);
  protected readonly data = inject<DeviceOverridesDialogData>(MAT_DIALOG_DATA);

  protected readonly query = signal('');
  protected readonly saving = signal(false);
  protected readonly devices = signal<DeviceOverrideDeviceRow[]>(this.data.devices);
  private readonly originalDevices = new Map<string, DeviceOverrideDeviceRow>(
    this.data.devices.map(d => [d.id, d]),
  );

  /** Local-only until `saveChanges()` runs. Toggling either one cascades to every device row
   * below, exactly like flipping the global toggle in the parent alarm-type table would. */
  protected readonly globalShowInPortal = signal(this.data.globalShowInPortal);
  protected readonly globalEmailNotification = signal(this.data.globalEmailNotification);

  /** A device counts as "overridden" the moment its values diverge from *its own* owning
   * partner's global — not `data.devices[].hasOverride`, which is frozen at dialog-open time
   * and never reflects toggles made in this session, so the description text above the table
   * would stay stuck at the original count (often 0) even after the user overrides a device
   * and saves. Compares against each row's own `ownerGlobalShowInPortal`/
   * `ownerGlobalEmailNotification`, not the dialog-wide `globalShowInPortal` signal — a Xemex
   * viewer's device list can span multiple partners, each with its own global, so a single
   * dialog-wide value would miscount devices owned by a partner other than the viewer's. */
  protected readonly overrideCount = computed(
    () =>
      this.devices().filter(
        d =>
          d.showInPortal !== d.ownerGlobalShowInPortal ||
          d.emailNotification !== d.ownerGlobalEmailNotification,
      ).length,
  );

  /** Only devices that currently diverge from their own owning partner's global are shown —
   * devices still following the global setting aren't overrides and don't belong in this list
   * (use the device's own alarm-settings tab to create one). No sort is applied here: every row
   * in the filtered set is already overridden, so there's nothing left to sort by, and this list
   * must stay in a stable order while the user is toggling — re-sorting after every edit (e.g.
   * after flipping one of the two global-setting toggles below) made rows jump around mid-edit. */
  protected readonly filteredDevices = computed(() => {
    const q = this.query().toLowerCase().trim();
    return this.devices().filter(d => {
      if (!this.isOverridden(d)) return false;
      if (!q) return true;
      return d.ldn.toLowerCase().includes(q) || d.address.toLowerCase().includes(q);
    });
  });

  /** Same "diverges from its own owning partner's global" check as overrideCount above. */
  private isOverridden(d: DeviceOverrideDeviceRow): boolean {
    return (
      d.showInPortal !== d.ownerGlobalShowInPortal ||
      d.emailNotification !== d.ownerGlobalEmailNotification
    );
  }

  private readonly selectedDeviceIds = signal<Set<string>>(new Set());

  /** Intersected with filteredDevices() so a row that stops being overridden (e.g. after a bulk
   * remove-override or the global-toggle cascade) drops out of the selection the moment it
   * disappears from the list, instead of lingering as a phantom selected id. */
  protected readonly selectedKeys = computed(() => {
    const visibleIds = new Set(this.filteredDevices().map(d => d.id));
    return new Set([...this.selectedDeviceIds()].filter(id => visibleIds.has(id)));
  });

  protected readonly selectionKey = (row: DeviceOverrideDeviceRow): string => row.id;

  protected readonly selectedRows = computed(() => {
    const ids = this.selectedKeys();
    return this.filteredDevices().filter(d => ids.has(d.id));
  });

  protected readonly bulkShowInPortal = computed(
    () => this.selectedRows().length > 0 && this.selectedRows().every(r => r.showInPortal),
  );
  protected readonly bulkEmailNotification = computed(
    () => this.selectedRows().length > 0 && this.selectedRows().every(r => r.emailNotification),
  );

  protected onSelectedKeysChange(keys: Set<string | number>): void {
    this.selectedDeviceIds.set(new Set(Array.from(keys, String)));
  }

  /** Local-only — nothing is persisted until `saveChanges()` runs. */
  protected setBulkShowInPortal(value: boolean): void {
    const ids = this.selectedKeys();
    this.devices.update(rows => rows.map(r => (ids.has(r.id) ? { ...r, showInPortal: value } : r)));
  }

  /** Local-only — nothing is persisted until `saveChanges()` runs. */
  protected setBulkEmailNotification(value: boolean): void {
    const ids = this.selectedKeys();
    this.devices.update(rows =>
      rows.map(r => (ids.has(r.id) ? { ...r, emailNotification: value } : r)),
    );
  }

  /** Resets the selected devices back to their own owning partner's global values — i.e. removes
   * their override. Selected rows therefore drop out of filteredDevices() right after, same as
   * toggling a single row's override off already does. */
  protected removeOverrideForSelection(): void {
    const ids = this.selectedKeys();
    this.devices.update(rows =>
      rows.map(r =>
        ids.has(r.id)
          ? { ...r, showInPortal: r.ownerGlobalShowInPortal, emailNotification: r.ownerGlobalEmailNotification }
          : r,
      ),
    );
    this.selectedDeviceIds.set(new Set());
  }

  /** True once the user has touched either global-setting toggle this session — drives the
   * warning that saving now cascades beyond just the devices listed here. */
  protected readonly globalSettingChanged = computed(
    () =>
      this.globalShowInPortal() !== this.data.globalShowInPortal ||
      this.globalEmailNotification() !== this.data.globalEmailNotification,
  );

  protected readonly columns: TableColumnConfig[] = [
    {
      field: 'ldn',
      label: 'alarmsPage.settings.deviceOverridesDialog.columns.device',
      type: TableColumnType.Text,
      bold: true,
      width: '13rem',
    },
    {
      field: 'address',
      label: 'alarmsPage.settings.deviceOverridesDialog.columns.address',
      type: TableColumnType.Text,
      muted: true,
      width: '17rem',
    },
    {
      field: 'showInPortal',
      label: 'alarmsPage.settings.deviceOverridesDialog.columns.showInPortal',
      type: TableColumnType.Toggle,
      width: '9rem',
    },
    {
      field: 'emailNotification',
      label: 'alarmsPage.settings.deviceOverridesDialog.columns.email',
      type: TableColumnType.Toggle,
      width: '11rem',
    },
  ];

  protected onToggleChange({ row, field }: { row: DeviceOverrideDeviceRow; field: string }): void {
    if (field === 'showInPortal') this.toggleShowInPortal(row);
    if (field === 'emailNotification') this.toggleEmailNotification(row);
  }

  protected onGlobalToggleChange({ field }: { field: string }): void {
    if (field === 'showInPortal') this.toggleGlobalShowInPortal();
    if (field === 'emailNotification') this.toggleGlobalEmailNotification();
  }

  protected toggleGlobalShowInPortal(): void {
    const value = !this.globalShowInPortal();
    this.globalShowInPortal.set(value);
    this.devices.update(rows => rows.map(r => ({ ...r, showInPortal: value })));
  }

  protected toggleGlobalEmailNotification(): void {
    const value = !this.globalEmailNotification();
    this.globalEmailNotification.set(value);
    this.devices.update(rows => rows.map(r => ({ ...r, emailNotification: value })));
  }

  /** Local-only — nothing is persisted until `saveChanges()` runs. */
  protected toggleShowInPortal(row: DeviceOverrideDeviceRow): void {
    this.devices.update(rows =>
      rows.map(r => (r.id === row.id ? { ...r, showInPortal: !r.showInPortal } : r)),
    );
  }

  /** Local-only — nothing is persisted until `saveChanges()` runs. */
  protected toggleEmailNotification(row: DeviceOverrideDeviceRow): void {
    this.devices.update(rows =>
      rows.map(r => (r.id === row.id ? { ...r, emailNotification: !r.emailNotification } : r)),
    );
  }

  /** Persists only the rows actually touched this session (individually toggled, or swept up by
   * a global-toggle cascade) — PUTting every row regardless of change hits the backend for
   * devices that never had an override to begin with, which it rejects. */
  protected saveChanges(): void {
    const changedRows = this.devices().filter(row => {
      const original = this.originalDevices.get(row.id);
      return (
        !original ||
        row.showInPortal !== original.showInPortal ||
        row.emailNotification !== original.emailNotification
      );
    });

    if (!changedRows.length) {
      this.dialogRef.close();
      return;
    }

    this.saving.set(true);

    forkJoin(
      changedRows.map(row =>
        this.alarmConfigService
          .updateDeviceOverride(this.data.configGroup, Number(row.deviceId), {
            // null means "inherit the global setting" — only send an explicit value for the
            // fields that actually diverge from it, otherwise the device would be persisted
            // as its own override (equal to the global value) and never show up as inheriting.
            // Compared against the row's own owning-partner global, not the dialog-wide
            // globalShowInPortal/globalEmailNotification signals (see overrideCount above) —
            // otherwise a device owned by another partner could get an explicit override
            // persisted (or wrongly cleared) based on the viewer's own global instead of its own.
            show_in_portal:
              row.showInPortal !== row.ownerGlobalShowInPortal ? row.showInPortal : null,
            email_enabled:
              row.emailNotification !== row.ownerGlobalEmailNotification
                ? row.emailNotification
                : null,
          })
          .pipe(
            map(() => null),
            catchError(error => of(error ?? new Error('unknown'))),
          ),
      ),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        this.saving.set(false);
        const failures = results.filter(r => r !== null);
        if (failures.length) {
          // eslint-disable-next-line no-console -- surface the real cause; the toast below is generic on purpose
          console.error('Device override save failed', failures);
          this.snackBar.open(
            this.translateService.instant('alarmsPage.settings.deviceOverridesDialog.saveError'),
            '',
            { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
          );
          return;
        }
        this.snackBar.open(
          this.translateService.instant('alarmsPage.settings.deviceOverridesDialog.saveSuccess'),
          '',
          { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
        );
        this.dialogRef.close();
      });
  }

  protected viewDeviceSettings(row: DeviceOverrideDeviceRow): void {
    this.dialogRef.close();
    this.router.navigate(['/', APP_PATHS.Devices, row.deviceId, 'alarm-settings']);
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
